import Image from "@tiptap/extension-image";

import { clampImageWidth, parseImageWidth, withImageWidth } from "./imageWidth";

export const ResizableImage = Image.extend({
  addNodeView() {
    return ({ node, editor, getPos }) => {
      let currentNode = node;
      let activeMove: ((event: MouseEvent) => void) | null = null;
      let activeUp: (() => void) | null = null;

      const dom = document.createElement("div");
      dom.className = "rn-image-wrapper";

      const img = document.createElement("img");
      dom.appendChild(img);

      const applyNode = (target: typeof node) => {
        const { cleanSrc, width } = parseImageWidth(
          (target.attrs.src as string) ?? ""
        );
        img.src = cleanSrc;
        img.alt = (target.attrs.alt as string) ?? "";
        img.style.width = width ? `${width}px` : "";
        img.style.height = "auto";
      };
      applyNode(currentNode);

      const detachDragListeners = () => {
        if (activeMove) document.removeEventListener("mousemove", activeMove);
        if (activeUp) document.removeEventListener("mouseup", activeUp);
        activeMove = null;
        activeUp = null;
      };

      let handle: HTMLSpanElement | null = null;
      const onMouseDown = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        detachDragListeners();
        const startX = event.clientX;
        const startWidth = img.getBoundingClientRect().width;
        const containerWidth =
          dom.parentElement?.getBoundingClientRect().width ?? startWidth;

        activeMove = (moveEvent: MouseEvent) => {
          if (moveEvent.buttons === 0) {
            activeUp?.();
            return;
          }
          const next = clampImageWidth(
            startWidth + (moveEvent.clientX - startX),
            containerWidth,
            img.naturalWidth,
            img.naturalHeight
          );
          img.style.width = `${next}px`;
        };
        activeUp = () => {
          detachDragListeners();
          if (editor.isDestroyed || typeof getPos !== "function") return;
          const finalWidth = clampImageWidth(
            img.getBoundingClientRect().width,
            containerWidth,
            img.naturalWidth,
            img.naturalHeight
          );
          const currentSrc = (currentNode.attrs.src as string) ?? "";
          const newSrc = withImageWidth(currentSrc, finalWidth);
          if (newSrc === currentSrc) return;
          const pos = getPos();
          if (typeof pos !== "number") return;
          const { state, dispatch } = editor.view;
          dispatch(
            state.tr.setNodeMarkup(pos, undefined, {
              ...currentNode.attrs,
              src: newSrc,
            })
          );
        };
        document.addEventListener("mousemove", activeMove);
        document.addEventListener("mouseup", activeUp);
      };

      if (editor.isEditable) {
        handle = document.createElement("span");
        handle.className = "rn-image-handle";
        handle.addEventListener("mousedown", onMouseDown);
        dom.appendChild(handle);
      }

      return {
        dom,
        update(updatedNode) {
          if (updatedNode.type.name !== currentNode.type.name) return false;
          currentNode = updatedNode;
          applyNode(updatedNode);
          return true;
        },
        selectNode() {
          dom.classList.add("rn-selected");
        },
        deselectNode() {
          dom.classList.remove("rn-selected");
        },
        destroy() {
          detachDragListeners();
          handle?.removeEventListener("mousedown", onMouseDown);
        },
      };
    };
  },
});
